/**
 * What a subject still lacks before a domain can be registered for it, grouped the way the
 * owner asked (2026-09-30): the gaps against the rules every domain follows ONCE, then only what
 * a registry or registrar needs beyond them, per registry ("WebNIC cần thêm: Mã bưu chính").
 * Every TLD is treated alike; a registry that needs nothing more is not named.
 *
 * `GET /subjects/{id}/registry-readiness` answers `baseline` and each registry's `extra`; an
 * older answer without them is grouped here (what every registry lists is the baseline). Its
 * `verification` says what verification the subject still lacks (owner decision 2026-10-01: an
 * individual by eKYC only — a signed declaration no longer counts —, an organisation by its
 * subject declaration covering its current data).
 * Pure functions: `npm test` covers them.
 */

import type { RegistrantProblem } from '../api/types';

/** How a subject is verified (subsystems/identity/facade/adapters/admin-api/src/admin/subjects.rs `verification_readiness`). */
export const VERIFICATION_REQUIRED = ['EKYC', 'SUBJECT_DECLARATION'] as const;
export const VERIFICATION_STATES = ['VERIFIED', 'MISSING', 'NOT_ACCEPTED', 'SIGN_AGAIN', 'PENDING'] as const;

/** How the subject is verified for a new operation (owner decision 2026-10-01). */
export interface VerificationAnswer {
  required: (typeof VERIFICATION_REQUIRED)[number];
  state: (typeof VERIFICATION_STATES)[number];
  message: string | null;
}

export interface ReadinessAnswer {
  baseline?: RegistrantProblem[];
  /** Absent on older servers: nothing is said about the verification. */
  verification?: VerificationAnswer;
  registries: { registry_code: string; registry_name: string; zones: string[]; problems: RegistrantProblem[]; extra?: RegistrantProblem[] }[];
}

export interface ReadinessGroups {
  /** Gaps against the rules for every domain. */
  baseline: RegistrantProblem[];
  /** Only registries that need more than the baseline. */
  extras: { registryCode: string; registryName: string; zones: string[]; problems: RegistrantProblem[] }[];
  /** The subjects to edit (the registrant first, then its representative). */
  subjects: { id: string; contact: string }[];
  /**
   * The verification still to do (owner decision 2026-10-01: an individual by eKYC only, an
   * organisation by its subject declaration), or null when verified / not said.
   */
  verification: VerificationAnswer | null;
  ready: boolean;
}

const key = (p: RegistrantProblem) => `${p.contact}|${p.subject_id}|${p.field}`;

export function groupReadiness(a: ReadinessAnswer): ReadinessGroups {
  let baseline = a.baseline;
  if (!baseline) {
    // Older answer: what every registry lists is the baseline.
    const [first, ...rest] = a.registries;
    baseline = first ? first.problems.filter((p) => rest.every((r) => r.problems.some((q) => key(q) === key(p)))) : [];
  }
  const inBaseline = new Set(baseline.map(key));
  const extras = a.registries
    .map((r) => ({
      registryCode: r.registry_code,
      registryName: r.registry_name,
      zones: r.zones,
      problems: (r.extra ?? r.problems).filter((p) => !inBaseline.has(key(p))),
    }))
    .filter((r) => r.problems.length > 0);
  const all = [...baseline, ...extras.flatMap((x) => x.problems)];
  const subjects: { id: string; contact: string }[] = [];
  for (const p of all) {
    // The extra data a TLD requires is completed on the domain (or the order), not on the subject.
    if (p.contact === 'DOMAIN') continue;
    if (!subjects.some((s) => s.id === p.subject_id)) subjects.push({ id: p.subject_id, contact: p.contact });
  }
  subjects.sort((x, y) => Number(x.contact === 'REPRESENTATIVE') - Number(y.contact === 'REPRESENTATIVE'));
  const verification = a.verification && a.verification.state !== 'VERIFIED' ? a.verification : null;
  return { baseline, extras, subjects, verification, ready: all.length === 0 && verification === null };
}

/**
 * The readiness for registering `domain`: the baseline, what the one connection of its zone (the
 * longest zone the name ends with) needs beyond it, and the verification — or null when no
 * connection takes the zone. The verification is not checked at quote or order: the registration
 * waits for it ("Chờ định danh").
 */
export function readinessFor(a: ReadinessAnswer, domain: string): ReadinessGroups | null {
  const name = domain.trim().toLowerCase().replace(/\.$/, '');
  const zone = (r: ReadinessAnswer['registries'][number]) => Math.max(-1, ...r.zones.filter((z) => name.endsWith(`.${z}`)).map((z) => z.length));
  const best = a.registries.reduce<ReadinessAnswer['registries'][number] | null>((m, r) => (zone(r) > (m ? zone(m) : -1) ? r : m), null);
  return best ? groupReadiness({ ...a, registries: [best] }) : null;
}

/** The distinct subjects a list of problems asks to edit, the registrant first. */
export function subjectsToEdit(problems: RegistrantProblem[]): { id: string; contact: string }[] {
  return groupReadiness({ baseline: problems, registries: [] }).subjects;
}
